import useStore from '@store/store';
import classNames from 'classnames';
import React from 'react';
import { useInView } from 'react-intersection-observer';
import { getChartColors } from 'utilities/helper';
import { hash } from 'utilities/helper';

import Tooltip from './Tooltip';

const colors = getChartColors();

interface Props {
  data: [];
  region: string;
  max: number;
  index: number;
  className?: string;
}

const HorizontalBarChart: React.FC<Props> = ({
  data = [],
  region = '',
  max = 100,
  index = 0,
  className = '',
}) => {
  const graphs = [
    'Your overall score',
    'Global (average)',
    `Regional (average): ${region}`,
    'Market leaders (average)',
  ];

  const { ref, inView } = useInView({
    threshold: 0.5,
  });

  const { generatingPDF } = useStore();

  return (
    <ul className={classNames('text-xs table w-full', className)} ref={ref}>
      {data.map(({ label, scores }, seriesIndex) => {
        return (
          <li className="table-row" key={label}>
            <h4 className="table-cell align-middle pr-4 text-right w-1/4">
              {label}
            </h4>
            <div className="table-cell py-3 w-3/4 group/tooltip">
              <dl className="flex flex-col gap-0.5">
                {graphs.map((graph, index) => (
                  <div
                    className="flex flex-row-reverse items-center gap-1.5"
                    key={hash([label, graph])}
                  >
                    <dt className="grow">
                      <div
                        className={classNames(
                          'h-2.5 transition-transform duration-500 ease-out origin-left',
                          [
                            generatingPDF || inView
                              ? 'scale-x-100'
                              : 'scale-x-0',
                          ]
                        )}
                        style={{
                          backgroundColor: colors[index],
                          width: `${(scores[index] / max) * 100}%`,
                          transitionDelay: `${seriesIndex * 75 + index * 75}ms`,
                        }}
                      />
                      <span className="sr-only">{graphs}</span>
                    </dt>
                    <dd className="font-medium w-6 text-right leading-none">
                      {scores[index]}
                    </dd>
                  </div>
                ))}
              </dl>
              <Tooltip
                title={label}
                index={index}
                items={graphs.map((graph, index) => ({
                  label: graph,
                  value: scores[index],
                  color: colors[index],
                }))}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default HorizontalBarChart;
